/**
 * Recheck booked appointment slots when the customer goes to checkout.
 *
 * A vet or grooming booking can sit in the bag for hours, and VetBuddy does not hold
 * the slot until the booking is made after payment. So on Checkout, every service
 * line's slot is asked about again; if it has gone, checkout stops and the customer
 * is told to book a new time. If VetBuddy cannot be reached the customer is let
 * through - the booking after payment checks the slot again.
 *
 * One order can hold several bookings, and VetBuddy knows none of them until after
 * payment, so the bag is also checked against itself: two bookings for the same pet,
 * or the same doctor/groomer, at overlapping times would both pass VetBuddy's check.
 */
(function () {
  'use strict';

  if (window.__ziglySlotRecheckInit) return;
  window.__ziglySlotRecheckInit = true;

  const CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"]';
  const RECHECK_TIMEOUT_MS = 8000;
  // Other checkout scripts re-click the button after ours lets it through, so a passed
  // recheck holds for a few seconds instead of a single click.
  const PASS_WINDOW_MS = 5000;
  const vetbuddySrc = document.currentScript && document.currentScript.dataset.vetbuddySrc;

  let vetbuddyLoading = null;
  let passUntil = 0;
  let checking = false;

  function loadVetBuddy() {
    if (window.ZiglyVetBuddy) return Promise.resolve(window.ZiglyVetBuddy);
    if (!vetbuddySrc) return Promise.reject(new Error('vetbuddy-api.js not configured'));
    if (!vetbuddyLoading) {
      vetbuddyLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = vetbuddySrc;
        script.onload = () => (window.ZiglyVetBuddy ? resolve(window.ZiglyVetBuddy) : reject(new Error('VetBuddy client missing')));
        script.onerror = () => {
          vetbuddyLoading = null;
          reject(new Error('vetbuddy-api.js failed to load'));
        };
        document.head.appendChild(script);
      });
    }
    return vetbuddyLoading;
  }

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('Slot recheck timed out')), ms)),
    ]);
  }

  function toMinutes(time) {
    const parts = String(time || '').trim().split(':');
    if (parts.length !== 2) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null;
  }

  function todayValue() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }

  /**
   * Is every 10-minute block of `range` ("11:30-12:00") still free in VetBuddy's
   * slot string ("11:30-11:40 OK|..., 11:40-11:50 Booked|...")?
   */
  function rangeIsFree(slotsString, range) {
    const [startText, endText] = String(range || '').split('-');
    const start = toMinutes(startText);
    let end = toMinutes(endText);
    if (start === null || end === null) return true;
    if (end <= start) end += 1440;

    let covered = start;
    const blocks = String(slotsString || '').split(',').map((slot) => {
      const parts = slot.trim().split(' ');
      const [from, to] = (parts[0] || '').split('-');
      return { from: toMinutes(from), to: toMinutes(to), free: (parts[1] || '').split('|')[0] === 'OK' };
    }).filter((block) => block.from !== null && block.to !== null);

    for (const block of blocks) {
      const blockEnd = block.to > block.from ? block.to : block.to + 1440;
      if (blockEnd <= start || block.from >= end) continue;
      if (!block.free) return false;
      if (block.from <= covered) covered = Math.max(covered, blockEnd);
    }
    return covered >= end;
  }

  function rangeMinutes(range) {
    const [startText, endText] = String(range || '').split('-');
    const start = toMinutes(startText);
    let end = toMinutes(endText);
    if (start === null || end === null) return null;
    if (end <= start) end += 1440;
    return { start, end };
  }

  function bookingName(entry) {
    return entry.props._packageName || entry.item.product_title;
  }

  function bookingWhen(props) {
    return `${props._appointmentDate.split('-').reverse().join('/')} at ${String(props._timeSlot).split('-')[0]}`;
  }

  function isSamePet(a, b) {
    if (a._patientId && b._patientId) return String(a._patientId) === String(b._patientId);
    const nameA = String(a._patientName || '').trim().toLowerCase();
    return !!nameA && nameA === String(b._patientName || '').trim().toLowerCase() &&
      (!a._petType || !b._petType || a._petType === b._petType);
  }

  /** Why the bag cannot be booked as it is, from the bag alone; '' when it can. */
  function bagProblem(lines) {
    const doubled = lines.find(({ item }) => item.quantity > 1);
    if (doubled) {
      return `Your ${bookingName(doubled)} booking on ${bookingWhen(doubled.props)} is in your bag ${doubled.item.quantity} times. Please keep one and book a new time for any other.`;
    }
    for (let i = 0; i < lines.length; i++) {
      for (let j = i + 1; j < lines.length; j++) {
        const a = lines[i].props;
        const b = lines[j].props;
        if (a._appointmentDate !== b._appointmentDate) continue;
        const rangeA = rangeMinutes(a._timeSlot);
        const rangeB = rangeMinutes(b._timeSlot);
        if (!rangeA || !rangeB || rangeA.end <= rangeB.start || rangeB.end <= rangeA.start) continue;
        const sameStaff = String(a._clinicId) === String(b._clinicId) && String(a._staff_id) === String(b._staff_id);
        if (sameStaff || isSamePet(a, b)) {
          return `Your ${bookingName(lines[i])} (${bookingWhen(a)}) and ${bookingName(lines[j])} (${bookingWhen(b)}) bookings overlap. Please remove one from your bag and book a new time.`;
        }
      }
    }
    return '';
  }

  function serviceLines(cart) {
    return (cart.items || [])
      .map((item) => ({ item, props: item.properties || {} }))
      .filter(({ props }) => props._productType === 'Service' && props._clinicId && props._staff_id && props._appointmentDate && props._timeSlot);
  }

  /** 'free' | 'gone' | 'unknown' for one cart line. */
  async function lineStatus(vetbuddy, props) {
    const date = props._appointmentDate;
    const start = toMinutes(String(props._timeSlot).split('-')[0]);
    if (date < todayValue()) return 'gone';
    if (date === todayValue()) {
      const now = new Date();
      if (start !== null && start <= now.getHours() * 60 + now.getMinutes()) return 'gone';
    }

    const [year, month, day] = date.split('-');
    const data = await vetbuddy.getStaffSlots(props._clinicId, props._staff_id, `${month}/${day}/${year}`);
    if (!data || data.responseCode !== 0 || data.status !== 'success') return 'unknown';
    const rota = (data.data || []).find((row) => String(row.StaffID) === String(props._staff_id));
    if (!rota) return 'gone';
    return rangeIsFree(rota.Slots, props._timeSlot) ? 'free' : 'gone';
  }

  function showMessage(button, text) {
    const container = button.closest('.cart__ctas') || button.parentElement;
    if (!container) {
      window.alert(text);
      return;
    }
    let message = container.querySelector('[data-slot-recheck-message]');
    if (!message) {
      message = document.createElement('p');
      message.dataset.slotRecheckMessage = '';
      message.setAttribute('role', 'alert');
      message.style.cssText = 'margin:0 0 8px;color:#c62828;font-size:13px;line-height:1.5;';
      container.insertBefore(message, container.firstChild);
    }
    message.textContent = text;
  }

  function clearMessage(button) {
    const container = button.closest('.cart__ctas') || button.parentElement;
    const message = container && container.querySelector('[data-slot-recheck-message]');
    if (message) message.remove();
  }

  function continueCheckout(button) {
    passUntil = Date.now() + PASS_WINDOW_MS;
    button.click();
  }

  /** The message that stops checkout, or '' to let it through. */
  async function recheck(button) {
    const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((response) => response.json());
    const lines = serviceLines(cart);
    if (!lines.length) return '';

    const problem = bagProblem(lines);
    if (problem) return problem;

    // Every line together: one at a time, a bag of several bookings outruns the timeout.
    const vetbuddy = await loadVetBuddy();
    const statuses = await Promise.all(lines.map(({ props }) => lineStatus(vetbuddy, props).catch(() => 'unknown')));
    const gone = lines.filter((line, index) => {
      if (statuses[index] === 'unknown') console.warn('[Zigly booking] Checkout slot recheck had no answer', line.props);
      return statuses[index] === 'gone';
    });
    if (!gone.length) return '';

    const list = gone.map((line) => `${bookingName(line)} on ${bookingWhen(line.props)}`).join(', ');
    return gone.length === 1
      ? `Your ${list} slot is no longer available. Please remove it from your bag and book a new time.`
      : `These slots are no longer available: ${list}. Please remove them from your bag and book new times.`;
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest(CHECKOUT_SELECTOR);
    if (!button) return;
    if (Date.now() < passUntil) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (checking) return;
    checking = true;
    clearMessage(button);

    withTimeout(recheck(button), RECHECK_TIMEOUT_MS)
      .then((problem) => {
        checking = false;
        if (!problem) {
          continueCheckout(button);
          return;
        }
        showMessage(button, problem);
      })
      .catch((error) => {
        // VetBuddy unreachable or slow: don't hold the sale; the booking after payment checks again.
        checking = false;
        console.warn('[Zigly booking] Checkout slot recheck skipped:', error.message);
        continueCheckout(button);
      });
  }, true);
})();
